import { useState } from "react";
import { Menu, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
const elevestLogo = "/elevest-logo.svg";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";
import { preloadRoute, preloadRouteIdle } from "@/lib/route-preload";

const preloadHandlers = (to: string) => ({
  onMouseEnter: () => preloadRouteIdle(to),
  onFocus: () => preloadRouteIdle(to),
  onMouseDown: () => preloadRoute(to),
  onPointerDown: () => preloadRoute(to),
  onTouchStart: () => preloadRoute(to),
});

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const links: { label: string; to: string }[] = [
    { label: t.navbar.home, to: "/" },
    { label: t.navbar.rechner, to: "/investitionsrechner" },
    { label: t.navbar.dealroom, to: "/plattform" },
    { label: t.navbar.wissen, to: "/wissen" },
    { label: t.navbar.faq, to: "/faq" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50" aria-label="Hauptnavigation">
      <div className="container mx-auto flex items-center justify-between h-24 px-4 lg:px-8">
        <Link to="/" className="flex items-center">
          <img src={elevestLogo} alt="Elevest Partners" width={256} height={128} fetchPriority="high" decoding="async" className="h-24 xl:h-32" />
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              {...preloadHandlers(l.to)}
              className="text-sm text-foreground/70 hover:text-foreground transition-colors"
            >
              {l.label}
            </Link>
          ))}

          <Button size="sm" className="rounded-full px-4 xl:px-5" asChild>
            <a href="/#lead">{t.navbar.cta}</a>
          </Button>
          <Button size="sm" variant="outline" className="rounded-full px-4 xl:px-5" asChild>
            <a href="https://propertydeal.pro" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
              {t.navbar.login}
              <ExternalLink size={12} />
            </a>
          </Button>
          <div className="flex-shrink-0">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Mobile toggle */}
        <div className="flex lg:hidden items-center gap-3">
          <div className="flex-shrink-0">
            <LanguageSwitcher />
          </div>
          <button onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-border bg-background px-4 pb-6 pt-4 space-y-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              {...preloadHandlers(l.to)}
              onClick={() => setOpen(false)}
              className="block text-sm text-foreground/70 hover:text-foreground"
            >
              {l.label}
            </Link>
          ))}

          <Button size="sm" className="w-full rounded-full" asChild>
            <a href="/#lead" onClick={() => setOpen(false)}>{t.navbar.cta}</a>
          </Button>
          <Button size="sm" variant="outline" className="w-full rounded-full" asChild>
            <a href="https://propertydeal.pro" target="_blank" rel="noopener noreferrer">{t.navbar.login}</a>
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
